import React, { Component, PureComponent } from "react"
import Draggable from "react-draggable"
import styled from "styled-components"
import terminalResponses from "./terminalResponses.json"
import "./TerminalDock.scss"

const FIRST_MESSAGE = "Connected to CyberPunk2022 local terminal."
const CHANGED_NICKNAME = "Your nickname is now: "
const HELP_TEXT = `/nick NewNickname - Change your nickname
/help - This Help
/shoot - How to fire the handgun
/status - Show terminal connection mode
/clear - Clear terminal output
`

const SHOOT_TEXT = "To shoot: Move your mouse next to the handgun and click."
const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])'
].join(",")

const focusWithoutScroll = element => {
  if (!element || typeof element.focus !== "function") return

  try {
    element.focus({ preventScroll: true })
  } catch (e) {
    element.focus()
  }
}

const normalizeReplyInput = input =>
  input
    .toLowerCase()
    .replace(/[’‘]/g, "'")
    .replace(/[^a-z0-9'\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim()

const pickRandom = choices =>
  choices[Math.floor(Math.random() * choices.length)]

const getLocalReply = input => {
  const normalizedInput = normalizeReplyInput(input)
  const intent = terminalResponses.intents.find(item =>
    item.inputs.includes(normalizedInput)
  )
  const choices = intent ? intent.responses : terminalResponses.fallbacks

  return pickRandom(choices)
}

const TerminalOverlayBase = ({
  isOpen,
  isExpanded,
  isMobile,
  viewportHeight,
  viewportTop,
  ...props
}) => <div {...props} />
const CursorBase = ({ hasValues, ...props }) => <div {...props} />
const InputFieldBase = React.forwardRef(({ show, ...props }, ref) => (
  <input ref={ref} {...props} />
))
const MessageTextBase = ({ color, ...props }) => <span {...props} />

const TerminalOverlay = styled(TerminalOverlayBase)`
  position: fixed;
  left: ${(props) => (props.isExpanded ? "258px" : "78px")};
  bottom: 28px;
  overflow: visible;
  display: ${(props) => (props.isOpen ? "flex" : "none")};
  z-index: 1005;

  @media screen and (max-width: 900px) {
    position: fixed;
    top: calc(
      ${(props) => props.viewportTop || 0}px +
        max(8px, env(safe-area-inset-top))
    );
    right: max(8px, env(safe-area-inset-right));
    bottom: auto;
    left: calc(64px + max(8px, env(safe-area-inset-left)));
    width: auto;
    height: min(
      300px,
      ${(props) => Math.max((props.viewportHeight || 300) - 64, 120)}px
    );
    margin: 0;
    overflow: hidden;

    > .handle {
      width: 100%;
      height: 100%;
      transform: none !important;
    }
  }
`

const TerminalContainer = styled.div`
  position: relative;
  flex: 1 1 auto;
  height: 100%;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  z-index: 99999;
`

const Outer = styled.div`
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
  width: 500px; // Add Media Query
  height: 270px;
  box-sizing: border-box;
  @media screen and (min-width: 1570px) {
    width: 620px; // Add Media Query
    height: 350px;
  }
  @media screen and (max-width: 900px) {
    width: 100%;
    height: 100%;
  }
  display: flex;
  z-index: 5000;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  background: black;
  color: white;
  opacity: 0.8;
`

const Cursor = styled(CursorBase)`
  position: relative;
  width: 100%;
  height: 100%;

  i {
    position: absolute;
    width: 3px;
    height: 50%;
    background-color: greenyellow;
    left: 5px;
    top: 9px;
    animation-name: blink;
    animation-duration: 800ms;
    animation-iteration-count: infinite;
    opacity: 1;
  }
  input:focus + i {
    display: ${(props) => (props.hasValues ? "none" : "block")};
  }

  @keyframes blink {
    from {
      opacity: 1;
    }
    to {
      opacity: 0;
    }
  }
`

const InputField = styled(InputFieldBase)`
  box-sizing: border-box;
  appearance: none;
  border-radius: 0;
  width: 100%;
  height: 100%;
  caret-color: ${(props) => (props.show ? "red" : "black")};
  color: #ff001c;
  z-index: 400;
  outline: none;
  border: none;
  font-size: 1em;
  background: black;
  font-family: "Rajdhani", sans-serif;
  font-weight: bold;
  padding-left: 5px;
`

const Input = styled.div`
  flex: 0 0 40px;
  height: 40px;
  z-index: 3000;
`

const Header = styled.div`
  cursor: pointer;
  position: relative;
  flex: 0 0 40px;
  height: 40px;
  background: darkslategrey;
  overflow: hidden;
  display: flex;
  background: linear-gradient(45deg, #111111 17%, #4c4c4c 42%, #111111 95%);
  -webkit-filter: blur(1px);
  filter: blur(1px);
  opacity: 0.4;
  z-index: 3000;
  border-top-left-radius: 3px;
  border-top-right-radius: 3px;
`

const FakeCloseButton = styled.button.attrs({
  className: "fakeButtons fakeClose not-draggable",
  type: "button"
})`
  appearance: none;
  padding: 0;
  cursor: pointer;
  touch-action: manipulation;

  &::after {
    content: "";
    position: absolute;
    top: -11px;
    left: -6px;
    width: 40px;
    height: 40px;
  }

  &:focus {
    outline: 2px solid #00e7ff;
    outline-offset: 2px;
  }
`

const Container = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: 1 1 auto;
  width: 100%;
  height: auto;
  min-height: 0;
  font-family: "Rajdhani", sans-serif;
  font-size: 1em;
  background: black;
  color: white;
  opacity: 0.8;

  color: rgba(120, 205, 110, 1);
  background-color: rgb(18, 27, 20);
  background-image: linear-gradient(transparent 50%, rgba(10, 15, 8, 0.1) 50%),
    radial-gradient(transparent 65%, rgba(2, 10, 1, 0.6) 100%);
  background-size: 15px, 100%;
`

const Nickname = styled.span`
  color: #0b806e;
`

const TextContainer = styled.div`
  margin: 5px;
`

const Text = styled(MessageTextBase)`
  color: ${(props) => (props.color === "bot" ? "green" : "red")};
`

const TextBot = styled.span`
  color: forestgreen;
`

const First = styled.span`
  color: mediumvioletred;
`

const Help1 = styled.div`
  margin-top: 2px;
  color: #37cf5a;
  font-size: 0.9em;
`

const Help = styled.span`
  color: #c7c700;
  font-size: 0.9em;
`

const Shoot = styled.span`
  color: #ff2f15;
  font-size: 1em;
`

class Messages extends PureComponent {
  render() {
    const { speaks, currentNick, text } = this.props

    return (
      <TextContainer>
        {speaks === "bot" && <Nickname>&lt;Phoenix&gt; </Nickname>}
        {speaks === "me" && <Nickname>&lt;{currentNick}&gt; </Nickname>}
        {(speaks === "me" || speaks === "bot") && (
          <Text color={speaks} user={speaks}>
            {text}
          </Text>
        )}
        {speaks === "first" && (
          <First>
            <span>{text}</span>
            <Help1>/help: Displays DOS Help.</Help1>
          </First>
        )}

        {speaks === "help" && <Help>Commands available:{text}</Help>}

        {speaks === "nick" && (
          <Help>
            {text} {currentNick}
          </Help>
        )}
        {speaks === "none" && (
          <Help>
            <Shoot>{text}</Shoot>
          </Help>
        )}
        {speaks === "afk" && (
          <TextBot>
            <Nickname>&lt;Phoenix&gt; </Nickname>
            {text}
          </TextBot>
        )}
      </TextContainer>
    )
  }
}

class TerminalDock extends Component {
  state = {
    input: "",
    messages: [
      {
        speaks: "first",
        text: FIRST_MESSAGE,
      },
    ],
    nick: "Anonymous",
  }

  textInput = React.createRef()
  terminalScroll = React.createRef()

  componentDidUpdate(prevProps, prevState) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.focusTextInput()
      if (this.state.messages.length === 1) {
        this.scrollToTop()
      } else {
        this.scrollDown()
      }
    }

    if (prevState.messages.length !== this.state.messages.length) {
      this.scrollDown()
    }
  }

  componentWillUnmount() {
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame)
    }
  }

  appendMessages = (messages, extraState = {}) => {
    this.setState((prevState) => ({
      ...extraState,
      input: "",
      messages: [...prevState.messages, ...messages],
    }))
  }

  scrollDown = () => {
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame)
    }
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null
      const scroller = this.terminalScroll.current
      if (scroller) {
        scroller.scrollTop = scroller.scrollHeight
      }
    })
  }

  scrollToTop = () => {
    if (this.scrollFrame) {
      window.cancelAnimationFrame(this.scrollFrame)
    }
    this.scrollFrame = window.requestAnimationFrame(() => {
      this.scrollFrame = null
      const scroller = this.terminalScroll.current
      if (scroller) {
        scroller.scrollTop = 0
      }
    })
  }

  focusTextInput = () => {
    setTimeout(() => {
      focusWithoutScroll(this.textInput.current)
    }, 0)
  }

  trapFocus = event => {
    if (event.key !== "Tab") return

    const dialog = event.currentTarget
    const focusable = Array.from(
      dialog.querySelectorAll(FOCUSABLE_SELECTOR)
    ).filter(element => element.getClientRects().length > 0)

    if (!focusable.length) {
      event.preventDefault()
      return
    }

    const first = focusable[0]
    const last = focusable[focusable.length - 1]
    const active = document.activeElement

    if (event.shiftKey && (active === first || !dialog.contains(active))) {
      event.preventDefault()
      focusWithoutScroll(last)
    } else if (
      !event.shiftKey &&
      (active === last || !dialog.contains(active))
    ) {
      event.preventDefault()
      focusWithoutScroll(first)
    }
  }

  clearInputField = () => {
    this.focusTextInput()
  }

  handleInputChange = (event) => {
    this.setState({
      input: event.target.value,
    })
  }

  handleInputKeyDown = (event) => {
    if (event.key === "Enter") {
      event.preventDefault()
      this.handleSubmit()
    }
  }

  handleSubmit = () => {
    const value = this.state.input.trim()

    if (!value) {
      return
    }

    const lowerValue = value.toLowerCase()
    const userMessage = {
      speaks: "me",
      text: value,
    }

    if (lowerValue === "/clear") {
      this.setState({
        input: "",
        messages: [
          {
            speaks: "first",
            text: FIRST_MESSAGE,
          },
        ],
      })
      return
    }

    if (lowerValue.indexOf("/nick") === 0) {
      const getNick = value.split(" ")
      this.appendMessages(
        [
          userMessage,
          {
            speaks: "nick",
            text: CHANGED_NICKNAME,
          },
        ],
        {
          nick: getNick[1] || "Invalid Nick",
        }
      )
      return
    }

    if (lowerValue.indexOf("/help") === 0) {
      this.appendMessages([
        userMessage,
        {
          speaks: "help",
          text: HELP_TEXT,
        },
      ])
      return
    }

    if (lowerValue.indexOf("/shoot") === 0) {
      this.appendMessages([
        userMessage,
        {
          speaks: "none",
          text: SHOOT_TEXT,
        },
      ])
      return
    }

    if (lowerValue.indexOf("/status") === 0) {
      this.appendMessages([
        userMessage,
        {
          speaks: "bot",
          text: "External API bridge: disabled. Mode: local/offline.",
        },
      ])
      return
    }

    this.appendMessages([
      userMessage,
      {
        speaks: "bot",
        text: getLocalReply(value),
      },
    ])
  }

  renderMessages = (stateMessages) => {
    if (stateMessages) {
      return stateMessages.map((message, i) => {
        return (
          <Messages
            currentNick={this.state.nick}
            key={i}
            speaks={message.speaks}
            text={message.text}
          />
        )
      })
    }
    return null
  }

  render() {
    const hasValues = Boolean(this.state.input)
    const { isMobile } = this.props

    return (
      <TerminalOverlay
        isExpanded={this.props.isExpanded}
        isOpen={this.props.isOpen}
        isMobile={isMobile}
        viewportHeight={this.props.viewportHeight}
        viewportTop={this.props.viewportTop}
        role={this.props.isOpen ? "dialog" : undefined}
        aria-modal={this.props.isOpen && isMobile ? "true" : undefined}
        aria-label={this.props.isOpen ? "Terminal" : undefined}
        onKeyDown={this.trapFocus}
      >
        <Draggable
          axis="both"
          cancel=".not-draggable"
          handle=".handle"
          defaultPosition={{ x: 10, y: 15 }}
          position={isMobile ? { x: 0, y: 0 } : null}
          disabled={isMobile}
          grid={[25, 25]}
          onStop={isMobile ? undefined : this.clearInputField}
          bounds="body"
        >
          <div className="handle">
            <Outer>
              <Header>
                <FakeCloseButton
                  onClick={this.props.onClose}
                  aria-label="Close terminal"
                  title="Close terminal"
                />
                <div className="fakeButtons fakeMinimize" />
                <div className="fakeButtons fakeZoom" />
              </Header>
              <Container
                className="not-draggable"
                onClick={isMobile ? undefined : this.focusTextInput}
                onMouseEnter={isMobile ? undefined : this.focusTextInput}
              >
                <div id="faux-terminal">
                  <div className="layer" />
                  <div className="overlay" />
                </div>
                <TerminalContainer ref={this.terminalScroll}>
                  <div id="chatbot">
                    {this.renderMessages(this.state.messages)}
                    <div className="not-draggable" style={{ padding: "2px" }} />
                  </div>
                </TerminalContainer>
              </Container>
              <Input className="not-draggable">
                <Cursor hasValues={hasValues}>
                  <InputField
                    show={hasValues}
                    ref={this.textInput}
                    value={this.state.input}
                    onChange={this.handleInputChange}
                    onKeyDown={this.handleInputKeyDown}
                    aria-label="Local terminal command"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    enterkeyhint="send"
                  />
                  {!hasValues && <i />}
                </Cursor>
              </Input>
            </Outer>
          </div>
        </Draggable>
      </TerminalOverlay>
    )
  }
}

export default TerminalDock
